/**
 * /admin layout — gates all child routes on admin role.
 *
 * Vercel rewrites `admin.nilboard.app/*` to `/admin/*`, so every page on the
 * admin subdomain runs through this layout. Non-admins are bounced to the
 * dashboard (or the login page if no session).
 */

import { FadeIn } from '@app/ui/motion';
import { Link, Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { getServerSession } from '../lib/auth-server';

export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ location }) => {
    const session = await getServerSession();

    // Cross-subdomain bounce: when accessed via admin.nilboard.app, Vercel
    // rewrites EVERY path back under /admin/*. A path-based redirect to
    // '/auth/login' would land on admin.nilboard.app/auth/login → rewritten
    // to /admin/auth/login → re-enters this beforeLoad → infinite loop.
    // Using an absolute URL pointing at www escapes the rewrite. Falls back
    // to same-origin in dev where VITE_APP_URL points at localhost (same
    // origin as admin in dev — no rewrite, no loop).
    const wwwUrl = import.meta.env.VITE_APP_URL;

    if (!session) {
      const returnUrl = `${location.pathname}${location.searchStr ?? ''}`;
      if (wwwUrl) {
        throw redirect({
          href: `${wwwUrl}/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`,
        });
      }
      throw redirect({ to: '/auth/login', search: { returnUrl } });
    }
    if (session.user.role !== 'admin' && session.user.role !== 'super-admin') {
      if (wwwUrl) {
        throw redirect({ href: `${wwwUrl}/dashboard` });
      }
      throw redirect({ to: '/dashboard' });
    }
    return { session };
  },
  component: AdminLayout,
});

function AdminLayout() {
  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="w-64 border-r bg-card p-4">
        <FadeIn>
          <h1 className="mb-6 text-lg font-bold">NilBoard Admin</h1>
          <nav className="space-y-1">
            <SidebarLink to="/admin" label="Dashboard" exact />
            <SidebarLink to="/admin/users" label="Athletes & Users" />
            <SidebarLink to="/admin/organizations" label="Universities & Agencies" />
            <SidebarLink to="/admin/deals" label="All Deals" />
            <SidebarLink to="/admin/brands" label="Brand Directory" />
            <SidebarLink to="/admin/audit-logs" label="Audit Logs" />
            <SidebarLink to="/admin/billing" label="Billing" />
            <SidebarLink to="/admin/config" label="Configuration" />
            <SidebarLink to="/admin/support" label="Support" />
          </nav>
        </FadeIn>
      </aside>
      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}

function SidebarLink({ to, label, exact }: { to: string; label: string; exact?: boolean }) {
  return (
    <Link
      to={to}
      activeOptions={exact ? { exact: true } : undefined}
      inactiveProps={{
        className:
          'block rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
      }}
      activeProps={{
        className: 'block rounded-md px-3 py-2 text-sm bg-muted text-foreground font-medium',
      }}
    >
      {label}
    </Link>
  );
}
